import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import { take } from 'rxjs/operators';
import {
  LocationsService,
  PendingPlace,
  PlaceDetail,
} from '../../../../services/locations.service';
import { REASON_FIRST, apiErrorText } from '../workspace/api-errors';
import { kindLabel } from './place-kinds';

/** A pending place, with what its detail adds: the name (the list carries only our own), kind and suburb. */
interface Row extends PendingPlace {
  name: string | null;
  kind: string | null;
  suburb: string | null;
}

/** How many place details load at once for the names. */
const CONCURRENCY = 4;

/**
 * Places waiting for review to go on the website (#253 step 3, #261): the ones a person asked for, and the ones the
 * matching pipeline's AI suggested for suburb pages, labelled with its reason. The AI only ever asks; a person
 * approves or rejects here, or opens the place for everything about it.
 *
 * The AI's reason is untrusted text: interpolated only.
 */
@Component({
  selector: 'app-suburb-pending',
  templateUrl: './suburb-pending.component.html',
  styleUrls: ['./suburb-pending.component.scss'],
})
export class SuburbPendingComponent implements OnInit {
  @Input() readOnly = false;
  /** The number waiting changed (approved, rejected, reloaded): the host's button shows it. */
  @Output() count = new EventEmitter<number>();

  rows: Row[] = [];
  /** Every request waiting, by source: the list holds at most 200 of them. */
  total = 0;
  bySource: { ai: number; user: number } | null = null;
  loading = false;
  error = '';
  message = '';
  busy = false;
  source: '' | 'ai' | 'user' = '';
  /** The place open in the panel, from this list. */
  open: number | null = null;
  readonly kindLabel = kindLabel;

  constructor(
    private api: LocationsService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.load();
  }

  private fail(e: unknown): string {
    return apiErrorText(e, 'something went wrong', REASON_FIRST);
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.api
      .pendingPlaces()
      .pipe(take(1))
      .subscribe({
        next: (r) =>
          this.zone.run(() => {
            this.rows = r.places.map((p) => ({
              ...p,
              name: p.displayName,
              kind: null,
              suburb: null,
            }));
            this.total = r.total ?? this.rows.length;
            this.bySource = r.bySource ?? null;
            this.loading = false;
            this.count.emit(this.total);
            this.cdr.detectChanges();
            this.loadDetails();
          }),
        error: (e) =>
          this.zone.run(() => {
            this.error = this.fail(e);
            this.loading = false;
            this.cdr.detectChanges();
          }),
      });
  }

  /** Names, kinds and suburbs, a few at a time. */
  private loadDetails(): void {
    const queue = [...this.rows];
    const next = () => {
      const row = queue.shift();
      if (!row) return;
      this.api
        .place(row.locationId)
        .pipe(take(1))
        .subscribe({
          next: (d) =>
            this.zone.run(() => {
              this.fill(row, d);
              next();
            }),
          error: () => next(),
        });
    };
    for (let i = 0; i < CONCURRENCY; i++) next();
  }

  private fill(row: Row, d: PlaceDetail): void {
    row.name = d.name;
    row.kind = d.kind;
    row.suburb = d.suburb;
    this.cdr.detectChanges();
  }

  get visible(): Row[] {
    return this.rows.filter((r) => !this.source || (r.requestSource ?? 'user') === this.source);
  }

  countOf(source: '' | 'ai' | 'user'): number {
    if (this.bySource) return source ? this.bySource[source] : this.total;
    return this.rows.filter((r) => !source || (r.requestSource ?? 'user') === source).length;
  }

  who(r: Row): string {
    return [r.firstName, r.lastName].filter(Boolean).join(' ') || 'someone';
  }

  decide(r: Row, action: 'approve' | 'reject', ev?: Event): void {
    ev?.stopPropagation();
    if (this.busy || this.readOnly) return;
    this.busy = true;
    this.message = '';
    this.api
      .setListing(r.locationId, action)
      .pipe(take(1))
      .subscribe({
        next: (d) =>
          this.zone.run(() => {
            this.busy = false;
            this.drop(r.locationId);
            this.message =
              action === 'approve'
                ? `${d.name} is on the website from the next publish of the suburbs near it.`
                : `${d.name} stays off the website.`;
            this.cdr.detectChanges();
          }),
        error: (e) =>
          this.zone.run(() => {
            this.busy = false;
            this.message = this.fail(e);
            this.cdr.detectChanges();
          }),
      });
  }

  private drop(locationId: number): void {
    const gone = this.rows.find((x) => x.locationId === locationId);
    if (!gone) return;
    this.rows = this.rows.filter((x) => x !== gone);
    this.total = Math.max(0, this.total - 1);
    if (this.bySource) this.bySource[gone.requestSource === 'ai' ? 'ai' : 'user']--;
    this.count.emit(this.total);
  }

  /** The open place changed: once it's no longer waiting for review, it leaves the list. */
  onChanged(d: PlaceDetail): void {
    const r = this.rows.find((x) => x.locationId === d.locationId);
    if (!r) return;
    if (d.listing !== 'pending') this.drop(d.locationId);
    else this.fill(r, d);
  }

  trackRow(_i: number, r: Row): number {
    return r.locationId;
  }
}
